import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionButtons,
  DataTable,
  type DataTableColumn,
} from '@enamelai/design-system'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import { Icon as FolderIcon } from '@enamelai/design-system/icons/Folder'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useFormsTableState } from '@components/pages/FormBuilder/Shared/useFormsTableState'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import './CompletedFormsTable.css'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import { SET_INPUT_NUMBER } from '@store/reducers/messageCenter.reducer'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import type { IContact } from '@dataTypes/contact.type'
import SelectContactModal from '@components/layout/Modals/SelectContact/SelectContactModal.component'

export interface CompletedFormEntry {
  /** Internal patient id; used for API and opening patient view */
  patientId: string
  publicPatientId?: string
  patientName: string
  patientMobile?: string
  completedFormsCount?: number
}

type CompletedFormRow = {
  id: string
  patientName: string
  completedFormsCount: number
}

interface CompletedFormsTableProps {
  data: CompletedFormEntry[]
  onOpenPatientForms?: (patientId: string, patientName?: string) => void
  /**
   * When set (e.g. from a "Form completed" notification deep-link), the
   * matching patient row is highlighted on mount, and again whenever this
   * value changes. Acts as the completed-table analogue of
   * `PendingFormsTable.initialExpandedPatientId` — the completed table has no
   * expandable rows, so we surface selection state instead.
   */
  initialSelectedPatientId?: string | null
}

const CompletedFormsTable: React.FC<CompletedFormsTableProps> = ({
  data,
  onOpenPatientForms,
  initialSelectedPatientId,
}) => {
  const dispatch = useAppDispatch()
  const contacts = useAppSelector((state) => state.contacts.data)
  const { showLoading } = useGlobalLoadingActions()
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    initialSelectedPatientId ?? null,
  )
  // Only re-apply an externally-supplied selection when it changes to a new id.
  const appliedInitialSelectedPatientIdRef = useRef<string | null>(
    initialSelectedPatientId ?? null,
  )
  useEffect(() => {
    const next = initialSelectedPatientId ?? null
    if (next == null) return
    if (appliedInitialSelectedPatientIdRef.current === next) return
    appliedInitialSelectedPatientIdRef.current = next
    setSelectedPatientId(next)
  }, [initialSelectedPatientId])
  const [showSelectContactModal, setShowSelectContactModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null)

  const rows = useMemo(
    (): CompletedFormRow[] =>
      data.map((entry) => ({
        id: entry.patientId,
        patientName: entry.patientName,
        completedFormsCount: entry.completedFormsCount ?? 0,
      })),
    [data],
  )
  const entryById = useMemo(
    () => new Map(data.map((entry) => [entry.patientId, entry])),
    [data],
  )
  const getSortValue = useCallback(
    (row: CompletedFormRow, columnId: string): string | number =>
      columnId === 'completedFormsCount'
        ? row.completedFormsCount
        : row.patientName,
    [],
  )
  const table = useFormsTableState(rows, getSortValue)

  const columns: DataTableColumn<CompletedFormRow>[] = [
    { id: 'patientName', label: 'Patient name', sortable: true },
    {
      id: 'completedFormsCount',
      label: 'Completed forms',
      sortable: true,
      width: 180,
    },
  ]

  const callPatient = (row: CompletedFormEntry): void => {
    const patientContact = contacts.find(
      (contact) => contact.patient_id === row.patientId,
    )
    if (patientContact) {
      setSelectedContact(patientContact)
      setShowSelectContactModal(true)
      return
    }
    const dialNumber = row.patientMobile
      ? normalizePhoneNumber(row.patientMobile)
      : ''
    if (dialNumber) {
      dispatch(SET_INPUT_NUMBER(dialNumber))
    }
    dispatch(setActiveSidePanel('phone'))
  }

  const renderActions = (row: CompletedFormRow): React.ReactElement | null => {
    const entry = entryById.get(row.id)
    if (!entry) return null
    return (
      <ActionButtons
        actions={[
          {
            id: 'view-forms',
            icon: <FolderIcon className="ds-action-button__svg" aria-hidden />,
            label: 'View forms',
            tone: 'primary',
            variant: 'solid',
            onClick: () => {
              if (onOpenPatientForms) {
                showLoading({ container: 'body' })
                onOpenPatientForms(entry.patientId, entry.patientName)
              }
            },
          },
          {
            id: 'call',
            icon: <CallIcon className="ds-action-button__svg" aria-hidden />,
            label: 'Phone',
            tone: 'primary',
            variant: 'outline',
            onClick: () => callPatient(entry),
          },
        ]}
      />
    )
  }

  if (data.length === 0) {
    return null
  }

  return (
    <div className="completed-forms-table-wrapper">
      <DataTable<CompletedFormRow>
        className="completed-forms-table"
        columns={columns}
        rows={table.pageRows}
        selectedRowIds={selectedPatientId ? [selectedPatientId] : []}
        sortColumnId={table.sortColumnId}
        sortDirection={table.sortDirection}
        onSort={table.onSort}
        pagination={table.pagination}
        showActions
        actionsLabel="Actions"
        renderActions={renderActions}
      />
      {showSelectContactModal && selectedContact && (
        <SelectContactModal
          show={showSelectContactModal}
          onHide={() => {
            setShowSelectContactModal(false)
            setSelectedContact(null)
          }}
          contactData={selectedContact}
        />
      )}
    </div>
  )
}

export default CompletedFormsTable
